import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FileUp, Loader2, AlertTriangle, CheckCircle2, ListOrdered, X, ListChecks } from 'lucide-react';
import { toast } from 'sonner';
import { thesisApi } from '@/api/thesisApi';

const SEV = {
  high: 'border-rose-500 text-rose-700 dark:text-rose-400',
  medium: 'border-amber-500 text-amber-700 dark:text-amber-400',
  low: 'border-slate-400 text-slate-600 dark:text-slate-300',
};
const STATUS = { present: 'text-emerald-600', thin: 'text-amber-600', missing: 'text-rose-600' };

export default function ManuscriptIntakePanel({ userId, projectId, onImported, onOpenPlan }) {
  const [data, setData] = useState(null);
  const [job, setJob] = useState(null);           // { job_id, status, step, steps, percent, progress }
  const [hidden, setHidden] = useState(false);    // progress card hidden, job keeps running
  const [createSections, setCreateSections] = useState(true);
  const [importRefs, setImportRefs] = useState(true);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await thesisApi.getManuscriptIntake(userId, projectId);
      if (res?.report) setData(res);
    } catch (e) { console.error(e); }
  }, [userId, projectId]);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const poll = useCallback((jobId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const j = await thesisApi.manuscriptIntakeJob(userId, projectId, jobId);
        setJob(j);
        if (j.status === 'ready') {
          stopPolling();
          setData(j.result);
          setJob(null);
          toast.success(`Draft analysed — ${j.result?.sections_created?.length || 0} sections, `
            + `${j.result?.references_imported || 0} references, `
            + `${j.result?.tasks_created || 0} writing tasks created`);
          onImported?.();
        } else if (j.status === 'failed') {
          stopPolling();
          setJob(null);
          toast.error(j.error || 'Intake failed');
        }
      } catch (e) {
        stopPolling();
        setJob(null);
        toast.error(e.response?.data?.detail || e.message);
      }
    }, 2000);
  }, [userId, projectId, onImported]);

  // Resume a run that is still going after a page reload
  useEffect(() => {
    let alive = true;
    (async () => {
      await load();
      try {
        const { jobs } = await thesisApi.manuscriptIntakeJobs(userId, projectId);
        const running = (jobs || []).find((j) => ['queued', 'running'].includes(j.status));
        if (running && alive) { setJob(running); poll(running.id || running.job_id); }
      } catch (e) { /* first run — no jobs yet */ }
    })();
    return () => { alive = false; stopPolling(); };
  }, [load, poll, userId, projectId]);

  const upload = async (file, inputEl) => {
    if (!file) return;
    try {
      const res = await thesisApi.manuscriptIntake(userId, projectId, file,
        createSections, importRefs);
      setJob({ job_id: res.job_id, status: 'queued', step: 0, steps: res.steps, percent: 0,
        progress: 'Queued — your draft is in the pipeline' });
      poll(res.job_id);
      setHidden(false);
      toast.info('Analysis running in the background — you can keep working');
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      if (inputEl) inputEl.value = '';
    }
  };

  const r = data?.report;
  const busy = !!job;
  const steps = job?.steps || [];

  return (
    <Card data-testid="manuscript-intake-panel">
      <CardHeader>
        <CardTitle className="font-serif flex flex-wrap items-center gap-2" style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
          <FileUp className="w-5 h-5 text-[#1d4ed8]" /> I already have a draft
          {r && <Badge variant="outline" className="text-[10px]" data-testid="intake-readiness">
            readiness {r.readiness_estimate}/100
          </Badge>}
        </CardTitle>
        <CardDescription>
          Upload your unpublished manuscript (DOCX, PDF, MD or TXT). InkPhD splits it into writing
          sections, imports its reference list and returns an honest gap analysis — then turns every
          gap into a writing task you can tick off in the Plan tab.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${busy ? 'bg-muted text-muted-foreground cursor-not-allowed' : 'bg-[#0b1a35] hover:bg-[#1d4ed8] text-white cursor-pointer'}`}
            data-testid="intake-upload-label">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />}
            {busy ? 'Analysing in the background…' : 'Upload my draft'}
            <input type="file" className="hidden" accept=".docx,.pdf,.md,.txt" disabled={busy}
              onChange={(e) => upload(e.target.files?.[0], e.target)} data-testid="intake-file-input" />
          </label>
          <label className="flex items-center gap-1.5 text-xs">
            <input type="checkbox" checked={createSections} className="accent-teal-700"
              onChange={(e) => setCreateSections(e.target.checked)} data-testid="intake-sections-toggle" />
            Create writing sections from it
          </label>
          <label className="flex items-center gap-1.5 text-xs">
            <input type="checkbox" checked={importRefs} className="accent-teal-700"
              onChange={(e) => setImportRefs(e.target.checked)} data-testid="intake-refs-toggle" />
            Import its reference list
          </label>
          {r && onOpenPlan && (
            <Button size="sm" variant="outline" onClick={onOpenPlan} data-testid="intake-open-plan">
              <ListChecks className="w-3.5 h-3.5 mr-1.5" /> Open the plan
            </Button>
          )}
        </div>

        {/* BACKGROUND PROGRESS */}
        {job && hidden && (
          <button type="button" onClick={() => setHidden(false)}
            className="text-[11px] inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[#1d4ed8]/40 text-[#1d4ed8] hover:bg-[#1d4ed8]/5"
            data-testid="intake-progress-restore">
            <Loader2 className="w-3 h-3 animate-spin" /> Analysis running — {job.percent || 0}% · show progress
          </button>
        )}
        {job && !hidden && (
          <div className="p-3 rounded-lg border border-[#1d4ed8]/30 bg-[#1d4ed8]/[0.04] space-y-3"
            data-testid="intake-progress">
            <div className="flex items-center justify-between gap-3">
              <div className="text-[13px] font-medium flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-[#1d4ed8]" />
                <span data-testid="intake-progress-label">{job.progress}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] tabular-nums text-muted-foreground"
                  data-testid="intake-progress-percent">{job.percent || 0}%</span>
                <Button size="icon" variant="ghost" className="h-6 w-6"
                  title="Hide — the job keeps running in the background"
                  onClick={() => setHidden(true)}
                  data-testid="intake-progress-hide">
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
            <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-[#1d4ed8] transition-all duration-700"
                style={{ width: `${Math.max(job.percent || 0, 4)}%` }} />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {steps.map((s, i) => {
                const idx = i + 1;
                const done = (job.step || 0) > idx;
                const active = (job.step || 0) === idx;
                return (
                  <span key={s} data-testid={`intake-step-${idx}`}
                    className={`text-[10px] px-2 py-0.5 rounded-full border ${done
                      ? 'border-emerald-500 text-emerald-700 dark:text-emerald-400'
                      : active ? 'border-[#1d4ed8] text-[#1d4ed8] font-medium'
                        : 'border-border text-muted-foreground'}`}>
                    {done ? '✓ ' : ''}{s}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {r && (
          <div className="space-y-4" data-testid="intake-report">
            <div className="flex flex-wrap gap-2 text-[11px]">
              <Badge variant="outline">{r.detected_type}</Badge>
              {r.applicable_guideline && <Badge variant="outline">{r.applicable_guideline}</Badge>}
              <Badge variant="outline">{data.stats?.words} words · {data.stats?.sections} sections</Badge>
              <Badge variant="outline">{data.stats?.citation_markers} in-text citations</Badge>
              <Badge variant="outline">{data.references_imported} references imported</Badge>
              {data.tasks_created != null && (
                <Badge variant="outline" data-testid="intake-tasks-created">
                  {data.tasks_created} writing tasks
                </Badge>
              )}
            </div>
            <p className="text-sm" data-testid="intake-summary">{r.summary}</p>

            {!!r.structure?.length && (
              <div className="flex flex-wrap gap-2" data-testid="intake-structure">
                {r.structure.map((s) => (
                  <span key={s.section} title={s.note}
                    className={`text-[11px] px-2 py-1 rounded border border-border ${STATUS[s.status] || ''}`}>
                    {s.section}: {s.status}
                  </span>
                ))}
              </div>
            )}

            {!!r.action_items?.length && (
              <div className="space-y-1.5" data-testid="intake-actions">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                  <ListOrdered className="w-3 h-3" /> Do this next
                </div>
                {r.action_items.map((a, i) => (
                  <div key={i} className="p-2 rounded border border-border">
                    <div className="text-[12px] font-medium">
                      P{a.priority} · {a.title}
                    </div>
                    <div className="text-[11px] text-muted-foreground">{a.detail}</div>
                  </div>
                ))}
              </div>
            )}

            {!!r.gaps?.length && (
              <div className="space-y-2" data-testid="intake-gaps">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> {r.gaps.length} gaps found
                </div>
                {r.gaps.map((g, i) => (
                  <div key={i} className="p-2 rounded border border-border space-y-1"
                    data-testid={`intake-gap-${i}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline" className={`text-[9px] ${SEV[g.severity] || SEV.low}`}>
                        {g.severity}
                      </Badge>
                      <span className="text-[12px] font-medium">{g.area}</span>
                    </div>
                    <p className="text-[11px]">{g.finding}</p>
                    <p className="text-[11px] text-muted-foreground italic">{g.why_it_matters}</p>
                    <p className="text-[11px] text-[#1d4ed8] dark:text-[#7aa9ff]">Fix: {g.fix}</p>
                  </div>
                ))}
              </div>
            )}

            {!!r.strengths?.length && (
              <div className="text-[11px] text-muted-foreground" data-testid="intake-strengths">
                <CheckCircle2 className="w-3 h-3 inline mr-1 text-emerald-600" />
                {r.strengths.join(' · ')}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
